import React from 'react';

/* The thing that stands between a render-time exception and a blank white page.

   Until this existed, ANY error thrown while rendering unmounted the whole React tree and the
   browser painted nothing: no message, no failing request, nothing in the network tab to point
   at. On 2026-09-09 a single unguarded `userInfo.networking_enabled` blanked /home for every
   role and every business unit, and the report that reached us was four words — "I get a white
   screen" — which is also exactly what a 500 on a backend route looks like. An hour went into
   telling those two apart.

   ★ The fallback deliberately uses NO imports beyond React: no i18n, no react-router Link, no
   CSS file, no Icon. Every one of those is a thing that can itself be broken (a missing
   translation bundle, a router outside its provider), and a fallback that throws while
   rendering the fallback puts us straight back to the blank page it exists to prevent. Inline
   styles, plain <a>, English only. This screen is the floor — it has to hold when nothing else
   does. */

const box = {
  maxWidth: 640,
  margin: '10vh auto',
  padding: '28px 24px',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  color: '#1a1a1a',
  background: '#fff',
  border: '1px solid #e3e3e3',
  borderRadius: 12,
  lineHeight: 1.5,
};

const btn = {
  display: 'inline-block',
  marginTop: 18,
  marginRight: 10,
  padding: '10px 18px',
  border: 0,
  borderRadius: 8,
  background: '#eb5a40',
  color: '#fff',
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep it in the console too: the details panel below is for whoever is holding the phone,
    // this line is for whoever opens devtools afterwards.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render failed:', error, info);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={box} role="alert">
        <h1 style={{ fontSize: 20, margin: '0 0 10px' }}>This screen failed to load</h1>
        <p style={{ margin: '0 0 6px' }}>
          Something went wrong while drawing this page. The rest of the app still works — try
          reloading, or go back to the start.
        </p>

        <button type="button" style={btn} onClick={() => window.location.reload()}>
          Reload
        </button>
        <a href="/" style={{ ...btn, background: '#555' }}>
          Back to start
        </a>

        {/* Open by default on purpose: the message is the single most useful thing a person can
            read back to us, and burying it behind a click is how "it just went white" happens. */}
        <details open style={{ marginTop: 22 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Technical details</summary>
          <pre
            style={{
              marginTop: 10,
              padding: 12,
              background: '#f6f6f6',
              border: '1px solid #e3e3e3',
              borderRadius: 8,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 260,
              overflow: 'auto',
            }}
          >
            {String((error && error.stack) || error)}
            {info && info.componentStack ? `\n${info.componentStack}` : ''}
          </pre>
        </details>
      </div>
    );
  }
}

export default ErrorBoundary;
